/**
 * File validation utilities for storage uploads
 */
import { STORAGE_CONFIG } from './config';
import type { StorageError } from './types';

export class ValidationError extends Error implements StorageError {
  code: string;
  serverResponse?: string;
  httpStatusCode?: number;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'ValidationError';
    this.code = code;
  }
}

/**
 * Validates a file for poster upload
 */
export const validatePosterFile = (file: File): void => {
  // Check file size
  if (file.size > STORAGE_CONFIG.maxFileSizeBytes) {
    const maxSizeMB = STORAGE_CONFIG.maxFileSizeBytes / (1024 * 1024);
    throw new ValidationError(
      `File size exceeds maximum limit of ${maxSizeMB}MB`,
      'storage/invalid-file-size',
    );
  }

  // Check MIME type
  if (!STORAGE_CONFIG.allowedMimeTypes.includes(file.type)) {
    throw new ValidationError(
      `File type ${file.type} is not supported. Allowed types: ${STORAGE_CONFIG.allowedMimeTypes.join(', ')}`,
      'storage/invalid-file-type',
    );
  }

  // Check file extension
  const extension = getFileExtension(file.name).toLowerCase();
  if (!STORAGE_CONFIG.supportedExtensions.includes(extension)) {
    throw new ValidationError(
      `File extension ${extension} is not supported. Allowed extensions: ${STORAGE_CONFIG.supportedExtensions.join(', ')}`,
      'storage/invalid-file-extension',
    );
  }

  // Basic file validation
  if (file.size === 0) {
    throw new ValidationError('File appears to be empty', 'storage/empty-file');
  }

  // Check for basic image file structure (magic bytes)
  validateImageMagicBytes(file);
};

/**
 * Extract file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.');
  return lastDot === -1 ? '' : filename.slice(lastDot);
};

/**
 * Generate a safe filename for storage
 */
export const generateSafeFileName = (originalName: string, suffix?: string): string => {
  const extension = getFileExtension(originalName);
  const baseName = originalName.slice(0, originalName.lastIndexOf('.')) || originalName;

  // Clean the base name
  const safeName = baseName
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();

  const timestamp = Date.now();
  const finalName = suffix
    ? `${safeName}_${suffix}_${timestamp}${extension}`
    : `${safeName}_${timestamp}${extension}`;

  return finalName;
};

/**
 * Validate image file by checking magic bytes
 */
const validateImageMagicBytes = (file: File): void => {
  // This is a basic check - in a production app you might want more thorough validation
  if (
    file.name.toLowerCase().endsWith('.jpg') ||
    file.name.toLowerCase().endsWith('.jpeg')
  ) {
    // JPEG files should start with FF D8
    return; // Skip magic byte check for now due to async nature
  }

  if (file.name.toLowerCase().endsWith('.png')) {
    // PNG files should start with 89 50 4E 47
    return; // Skip magic byte check for now due to async nature
  }

  if (file.name.toLowerCase().endsWith('.webp')) {
    // WebP files should contain WEBP signature
    return; // Skip magic byte check for now due to async nature
  }
};

/**
 * Check if file is likely an image based on content
 */
export const isImageFile = (file: File): boolean => {
  return (
    file.type.startsWith('image/') && STORAGE_CONFIG.allowedMimeTypes.includes(file.type)
  );
};

/**
 * Validate alt text for accessibility
 */
export const validateAltText = (altText?: string): void => {
  if (altText && altText.length > 200) {
    throw new ValidationError(
      'Alt text cannot exceed 200 characters',
      'storage/invalid-alt-text',
    );
  }

  if (altText && altText.trim().length === 0) {
    throw new ValidationError(
      'Alt text cannot be empty if provided',
      'storage/empty-alt-text',
    );
  }
};
